export type TokenKind =
  | 'word'
  | 'name'
  | 'str'
  | 'int'
  | 'num'
  | 'quantity'
  | 'duration'
  | 'date'
  | 'date_time'
  | 'time'
  | '('
  | ')'
  | '['
  | ']'
  | ','
  | ';'
  | ':'
  | '.'
  | '..'
  | '=='
  | '!='
  | '<'
  | '<='
  | '>'
  | '>='
  | '-'
  /** Search-bar text the lexer cannot read, kept raw; no grammar rule accepts it. */
  | 'unreadable';

export interface Token {
  kind: TokenKind;
  /** Decoded text for `word`, `name` and `str`; the source text for every other kind. */
  value: string;
  start: number;
  end: number;
  number?: number;
  unit?: string;
}

export class DslError extends Error {
  constructor(
    readonly start: number,
    readonly end: number,
    readonly expected: string,
    readonly found: string
  ) {
    super(`Expected ${expected}, found ${found}`);
  }
}

export interface LexOptions {
  /**
   * Search-bar mode: `-field:` negation, non-ASCII letters, interior hyphens in words, and text it
   * cannot read kept as `unreadable` tokens.
   */
  searchBar?: boolean;
}

const PUNCTUATION: [string, TokenKind][] = [
  ['..', '..'],
  ['!=', '!='],
  ['<=', '<='],
  ['>=', '>='],
  ['==', '=='],
  ['(', '('],
  [')', ')'],
  ['[', '['],
  [']', ']'],
  [',', ','],
  [';', ';'],
  [':', ':'],
  ['.', '.'],
  ['<', '<'],
  ['>', '>']
];
const DATETIME_PREFIX = /^\d{4}-\d{2}-\d{2}T/;
const DATETIME_FULL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})/;
const DATE = /^\d{4}-\d{2}-\d{2}/;
const TIME = /^\d{1,2}:\d{2}:\d{2}/;
/** Longest suffixes first, as in the data layer lexer. */
const UNIT_SUFFIXES = ['mm', 'km', 'cm', 'kg', 'm', 'g', 't'];

/** At least one component, and a `T` only before a time component: `P` and `PT` are refused. */
const ISO_DURATION =
  /^P(?=\d|T\d)((\d+)W)?((\d+)D)?(T(?=\d)((\d+)H)?((\d+)M)?((\d+(?:\.\d+)?)S)?)?$/;

export function isIsoDuration(text: string): boolean {
  return ISO_DURATION.test(text);
}

function isDigit(ch: string | undefined): boolean {
  return ch !== undefined && ch >= '0' && ch <= '9';
}

export function tokenize(input: string, options: LexOptions = {}): Token[] {
  const wordChar = options.searchBar ? /[\p{L}\p{N}_]/u : /[A-Za-z0-9_]/;
  const tokens: Token[] = [];
  let pos = 0;
  while (pos < input.length) {
    if (/\s/.test(input[pos])) {
      pos++;
      continue;
    }
    let token: Token;
    try {
      token = scanToken(input, pos, wordChar, options);
    } catch (error) {
      if (!options.searchBar || !isRecoverable(error, input, pos)) throw error;
      token = unreadable(input, pos);
    }
    tokens.push(token);
    pos = token.end;
  }
  return tokens;
}

/**
 * The search bar reads what it cannot tokenize as free text. An unterminated quote leaves nothing to
 * split, and `{` opens the stored JSON of grammar this build cannot spell, which must not turn into a
 * search for its words.
 */
function isRecoverable(error: unknown, input: string, pos: number): error is DslError {
  return error instanceof DslError && error.found !== 'end of input' && input[pos] !== '{';
}

/**
 * From `start` up to the next space, parenthesis, quote or `{`, or the whole quoted run holding a bad
 * escape.
 */
function unreadable(input: string, start: number): Token {
  const quote = input[start];
  let end = start + 1;
  if (quote === '"' || quote === '`') {
    while (end < input.length && input[end] !== quote) end += input[end] === '\\' ? 2 : 1;
    if (end >= input.length) {
      throw new DslError(
        start,
        input.length,
        quote === '"' ? 'a closing quote' : 'a closing backtick',
        'end of input'
      );
    }
    end++;
  } else {
    while (end < input.length && !/[\s(){"`]/.test(input[end])) end++;
  }
  return { kind: 'unreadable', value: input.slice(start, end), start, end };
}

function scanToken(
  input: string,
  pos: number,
  wordChar: RegExp,
  options: LexOptions
): Token {
  const ch = input[pos];
  const next = input[pos + 1];
  if (ch === '"' || ch === '`') return scanQuoted(input, pos, ch);
  const punctuation = PUNCTUATION.find(([text]) => input.startsWith(text, pos));
  if (punctuation) {
    const [text, kind] = punctuation;
    return { kind, value: text, start: pos, end: pos + text.length };
  }
  if (isDigit(ch) || (ch === '-' && isDigit(next))) {
    const token = scanNumber(input, pos);
    // A search-bar number run straight into a word (`12abc`, `20times`, a uuid, `2024-12`) is
    // one bare word: split, it would silently search for `12 abc` or end a shorthand value early.
    if (options.searchBar && continuesWord(input, token.end, wordChar)) {
      const end = scanSearchWord(input, token.end, wordChar);
      return { kind: 'word', value: input.slice(pos, end), start: pos, end };
    }
    return token;
  }
  if (ch === '-' && options.searchBar) {
    return { kind: '-', value: '-', start: pos, end: pos + 1 };
  }
  const durationEnd =
    ch === 'P' && next !== undefined && (isDigit(next) || next === 'T')
      ? scanDuration(input, pos)
      : null;
  // Search-bar values and free text are bare words, so `P1` or `PTA` there is a word rather
  // than a malformed duration.
  if (
    durationEnd !== null &&
    !(options.searchBar && !isIsoDuration(input.slice(pos, durationEnd)))
  ) {
    return {
      kind: 'duration',
      value: input.slice(pos, durationEnd),
      start: pos,
      end: durationEnd
    };
  }
  if (wordChar.test(ch)) {
    const end = options.searchBar
      ? scanSearchWord(input, pos, wordChar)
      : scanWhile(input, pos, wordChar);
    return { kind: 'word', value: input.slice(pos, end), start: pos, end };
  }
  throw new DslError(pos, pos + 1, 'a valid token', ch);
}

/** Fractional seconds (`PT1.5S`) included; a `..` range right after the duration is not one. */
function scanDuration(input: string, start: number): number {
  let end = scanWhile(input, start, /[A-Za-z0-9]/);
  while (input[end] === '.' && isDigit(input[end - 1]) && isDigit(input[end + 1])) {
    end = scanWhile(input, end + 1, /[A-Za-z0-9]/);
  }
  return end;
}

function continuesWord(input: string, at: number, wordChar: RegExp): boolean {
  return (
    wordChar.test(input[at] ?? '') ||
    (input[at] === '-' && wordChar.test(input[at + 1] ?? ''))
  );
}

/** Search-bar words keep interior hyphens (`Saint-Denis`, `c-1`); a leading `-` still negates. */
function scanSearchWord(input: string, start: number, wordChar: RegExp): number {
  let end = scanWhile(input, start, wordChar);
  while (input[end] === '-' && wordChar.test(input[end + 1] ?? '')) {
    end = scanWhile(input, end + 1, wordChar);
  }
  return end;
}

function scanWhile(input: string, start: number, pattern: RegExp): number {
  let end = start;
  while (end < input.length && pattern.test(input[end])) end++;
  return end;
}

function scanQuoted(input: string, start: number, quote: '"' | '`'): Token {
  let value = '';
  let i = start + 1;
  while (i < input.length) {
    const ch = input[i];
    if (ch === quote) {
      return { kind: quote === '"' ? 'str' : 'name', value, start, end: i + 1 };
    }
    if (ch === '\\') {
      const escaped = input[i + 1];
      if (escaped === undefined) break;
      if (escaped !== quote && escaped !== '\\') {
        throw new DslError(
          i,
          i + 2,
          `a recognized escape (\\${quote} or \\\\)`,
          `\\${escaped}`
        );
      }
      value += escaped;
      i += 2;
      continue;
    }
    value += ch;
    i++;
  }
  throw new DslError(
    start,
    input.length,
    quote === '"' ? 'a closing quote' : 'a closing backtick',
    'end of input'
  );
}

function scanNumber(input: string, start: number): Token {
  const rest = input.slice(start);
  if (DATETIME_PREFIX.test(rest)) {
    const match = DATETIME_FULL.exec(rest);
    if (!match)
      throw new DslError(start, input.length, 'a complete RFC 3339 timestamp', rest);
    return { kind: 'date_time', value: match[0], start, end: start + match[0].length };
  }
  const date = DATE.exec(rest);
  if (date) return { kind: 'date', value: date[0], start, end: start + date[0].length };
  const time = TIME.exec(rest);
  if (time) return { kind: 'time', value: time[0], start, end: start + time[0].length };

  let i = 0;
  let fractional = false;
  if (rest[i] === '-') i++;
  while (isDigit(rest[i])) i++;
  if (rest[i] === '.' && isDigit(rest[i + 1])) {
    fractional = true;
    i++;
    while (isDigit(rest[i])) i++;
  }
  if (rest[i] === 'e' || rest[i] === 'E') {
    let j = i + 1;
    if (rest[j] === '+' || rest[j] === '-') j++;
    if (isDigit(rest[j])) {
      fractional = true;
      i = j;
      while (isDigit(rest[i])) i++;
    }
  }
  const text = rest.slice(0, i);
  const unit = UNIT_SUFFIXES.find((suffix) => rest.startsWith(suffix, i));
  if (unit) {
    return {
      kind: 'quantity',
      value: text + unit,
      number: Number(text),
      unit,
      start,
      end: start + i + unit.length
    };
  }
  return {
    kind: fractional ? 'num' : 'int',
    value: text,
    number: Number(text),
    start,
    end: start + i
  };
}
